import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import PatientHelper from '../../../controls/helpers/patientHelper';
import {useDispatch, useSelector} from 'react-redux';
import {ctxAgeUnknown} from '../../../store/reducers/patientContext';
import React, {useEffect, useState} from 'react';
import {setSnackbar} from '../../../store/reducers/globals';
import {getPatientFlagValue} from '../../../pages/ward/mainScreen/util/patientFlagsUtil';
import {getSafeguardingLevel, isPaedsPatient, selectSafeLevelColor} from '../../../util/patientContextUtil';
import {AgyleDeceasedIcon} from '../../icons/agyle';
import moment from 'moment/moment';
import {getConfig} from '../../../util/configUtil';
import {AKAIcon, AlertIcon, Bear} from '../../icons/icons';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import usePatientLocation from '../../../hooks/usePatientLocation';

const useStyles = makeStyles((theme) =>
    ({
        root: {
            backgroundColor: theme.palette.inPatient.inputBackground,
            borderRadius: 10,
            padding: 10,
            fontSize: 12,
        },
        divRow: {
            display: 'flex',
            flexWrap: 'nowrap',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
        },
        label: {
            color: theme.palette.inPatient.questionText,
            paddingRight: 5,
        },
        cpis: {
            display: 'flex',
            width: 13,
            height: 13,
            padding: 10,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.palette.attendanceType.ed,
            borderRadius: 4,
            fontSize: 8,
            userSelect: 'none',
        },

        gender: {
            borderRadius: 6,
            color: '#222425',
            display: 'flex',
            padding: 2,
            '&.Male': {
                backgroundColor: '#92bfe2',
            },
            '&.Female': {
                backgroundColor: '#f4bdd1',
            },
        },
        location: {
            backgroundColor: '#03C3EC',
            borderRadius: 3,
            padding: '0 5px',
            color: '#222425',
            fontWeight: 600,
            fontSize: 10,
            display: 'flex',
            alignItems: 'center',
        },
    }),
);

const PatientDemographicBanner = ({patient}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // Patient location
    const [location, loading] = usePatientLocation(patient);

    // Selectors
    const ptAgeUnknown = useSelector(ctxAgeUnknown);

    // Icon state settings
    const [safeguardingLevel, setSafeguardingLevel] = useState(null);
    const [deceased, setDeceased] = useState(false);

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const safeguardingLevelMandatoryConfig = getConfig('safeguardingLevelMandatory', false);

    // Check safeguarding level for children patients and deceased flag for all type patients
    useEffect(() => {
        if (patient?.metadata?.patientFlags) {
            setDeceased(getPatientFlagValue(patient, 'deceased'));
        }

        if (patient && isPaedsPatient(patient.patient.dob)) {
            setSafeguardingLevel(getSafeguardingLevel(patient));
        }
    }, [patient]);

    const getLabel = (text) => {
        return <span className={classes.label}>{t(text)}:</span>;
    };

    const handleCopyPtNumber = (number) => {
        if (!navigator.clipboard || !number) return;
        navigator.clipboard.writeText(number)
            .then(() => {
                dispatch(setSnackbar({
                    type: 'success',
                    message: 'The patient number has been copied to the clipboard.',
                }));
            });
    };

    const getNumberOf = (type) => {
        const patientObject = patient?.patient;
        return patientObject?.[type] == null ? false : patientObject?.[type].length;
    };

    const getPatientHospitalNumber = () => {
        const ptNumber = patient?.patient ? PatientHelper.getPatientHospitalNumber(patient.patient) : null;
        return (
            <div>
                {getLabel('Hosp No')}
                <span style={{cursor: 'pointer'}} onClick={() => handleCopyPtNumber(ptNumber)}>
                    {ptNumber ?? ''}
                </span>
            </div>
        );
    };

    const getPatientNHSNumber = () => {
        const ptNumber = patient?.patient ? t(PatientHelper.getPatientNSHNumber(patient.patient)) : null;
        return (
            <div>
                {getLabel('NHS No')}
                <span style={{cursor: 'pointer'}} onClick={() => handleCopyPtNumber(ptNumber)}>
                    {ptNumber ?? ''}
                </span>
            </div>
        );
    };

    const getPatientDoB = () => {
        let text;
        if (ptAgeUnknown) text = t('Unknown');
        else {
            const age = patient?.patient ? PatientHelper.getPatientAge(patient.patient) : '';
            const dob = patient?.patient ? PatientHelper.getPatientDob(patient.patient) : '';
            text = `${dob} (${age})`;
        }

        return (
            <div>
                {getLabel('DOB')}

                <span>{text}</span>
            </div>
        );
    };

    const getPatientDoD = () => {
        if (!deceased) return null;

        return (
            <div>
                {getLabel('DOD')}
                <span>{patient?.patient ? moment(patient.patient.dod).format(defaultDateTimeFormat) : ''}</span>
            </div>
        );
    };

    const getPatientLocation = () => {
        if (!loading && !location?.__id) return null;

        return (
            <div style={{display: 'flex'}}>
                {getLabel('Location')}

                {loading &&
                    <span>{t('loading...')}</span>
                }

                {location && !loading &&
                    <div className={classes.location}>
                        {location?.trudDescription}
                    </div>
                }
            </div>
        );
    };

    const getPatientName = () => {
        return `${patient?.patient?.familyName?.toUpperCase()}, ${patient?.patient?.firstName}`;
    };

    const getPatientGender = () => {
        let icon;
        let color;
        switch (patient?.patient?.gender) {
            case 'Female':
                icon = <FemaleIcon style={{fontSize: 12}}/>;
                color = '#F4BDD1';
                break;
            case 'Male':
                icon = <MaleIcon style={{fontSize: 12}}/>;
                color = '#92BFE2';
                break;
            default:
                icon = <QuestionMarkIcon style={{fontSize: 12}}/>;
                color = '#acafb3';
        }

        return (
            <div className={classes.gender} style={{backgroundColor: color}}>
                {icon}
            </div>
        );
    };

    const getPatientAlert = () => {
        if (!getNumberOf('alerts')) return null;
        return <AlertIcon style={{fontSize: 12}}/>;
    };

    const getPatientAka = () => {
        if (!getNumberOf('names')) return null;
        return <AKAIcon style={{fontSize: 12}}/>;
    };

    const getPeadSafeguarding = () => {
        if (safeguardingLevel && safeguardingLevelMandatoryConfig)
            return <Bear style={{color: selectSafeLevelColor(safeguardingLevel), fontSize: 12}}/>;
        else
            return null;
    };

    const getPatientDeceased = () => {
        if (!deceased) return null;
        return <AgyleDeceasedIcon style={{fontSize: 12}}/>;
    };

    const getPatientCPIS = () => {
        if (patient?.admission?.cpisFlag !== 'Y') return null;
        return (
            <div className={classes.cpis}>
                {t('CPIS')}
            </div>
        );
    };

    return (
        <div className={classes.root}>
            <div className={classes.divRow}>
                {getPatientName()}
                {getPatientGender()}
                {getPatientAlert()}
                {getPatientAka()}
                {getPeadSafeguarding()}
                {getPatientDeceased()}
                {getPatientCPIS()}
                <div style={{display: 'flex', gap: 10, marginLeft: 'auto'}}>
                    {getPatientLocation()}
                    {getPatientDoD()}
                </div>
            </div>

            <div className={classes.divRow}>
                {getPatientHospitalNumber()}
                {getPatientNHSNumber()}
                {getPatientDoB()}
            </div>
        </div>
    );
};

export default React.memo(PatientDemographicBanner);